import { clientConfig } from '@/config/clientConfig';
import type { MilestoneEvent } from '../api/jobDetailApi';

export interface MilestoneDef {
  /** The exact work-order status name recorded in audit_trail `new_value`. */
  readonly matchKey: string;
  /** The label shown to the user (may differ from the backend status name). */
  readonly label: string;
}

/**
 * The work-order milestones shown on the job Overview, in lifecycle order.
 *
 * `matchKey` must equal the backend work-order status name stored in
 * `audit_trail.new_value` (e.g. 'Planned', 'En Route'); `label` is the wording
 * shown to the user. Keep this list ordered — {@link computeMilestoneStates}
 * relies on the array order to decide which milestones a job has passed.
 */
export const MILESTONES: readonly MilestoneDef[] = [
  { matchKey: 'New', label: 'New' },
  { matchKey: 'Ready for Planning', label: 'Ready for Planning' },
  { matchKey: 'Planned', label: 'Scheduled' },
  { matchKey: 'En Route', label: 'Engineer on Route' },
  { matchKey: 'On Site', label: 'On Site' },
  { matchKey: 'Engineering Completed', label: 'Engineering Completed' },
  { matchKey: 'Validation Requested', label: 'Validation Requested' },
  { matchKey: 'Completed', label: 'Completed' },
];

export interface MilestoneState {
  readonly def: MilestoneDef;
  readonly completed: boolean;
}

/**
 * Derives the completed state of every milestone from the work order's
 * status-change history.
 *
 * Order-aware: a milestone is complete if it — or any *later* milestone in the
 * lifecycle — has been reached. This mirrors real progression (a job cannot be
 * "Completed" without having been "On Site") and is resilient to intermediate
 * status transitions that were never recorded in the audit trail.
 */
export function computeMilestoneStates(events: MilestoneEvent[]): MilestoneState[] {
  const reached = new Set(events.map((e) => e.new_value).filter((v): v is string => v !== null));

  let highestReachedIndex = -1;
  MILESTONES.forEach((milestone, index) => {
    if (reached.has(milestone.matchKey)) {
      highestReachedIndex = index;
    }
  });

  const { visibleMatchKeys, labelOverrides } = clientConfig.milestones;
  const visible = visibleMatchKeys
    ? MILESTONES.filter((def) => visibleMatchKeys.includes(def.matchKey))
    : MILESTONES;

  return visible.map((def) => {
    const index = MILESTONES.indexOf(def);
    const label = labelOverrides?.[def.matchKey] ?? def.label;
    return {
      def: label === def.label ? def : { ...def, label },
      completed: index <= highestReachedIndex,
    };
  });
}
